/**
 * SessionTimelineShowcase — the coach session timeline rendered from demo data
 * for the Coaching OS marketing surface. Reuses the real `CoachSessionRow` via
 * the lifted `CoachSessionTimelineView`, so the showcase shows the genuine
 * product, not a mock. Rows stagger in on load; hovering or focusing a row
 * expands a detail strip revealing its notes & homework counts (CSS
 * `group-hover` + `group-focus-within`, so keyboard users get the reveal too).
 *
 * Edge-safe and inert: no router/query/encryption providers, no real
 * navigation, drawers, toast or pagination — every row callback is a no-op.
 * Renders inside the shared `ShowcaseFrame` ("feature" variant) so it reads as a
 * contained product window.
 *
 * Aliases: session timeline showcase, sessions surface showcase, session list.
 */

import { motion } from "motion/react";
import { useMemo, type ReactNode } from "react";

import {
  CoachSessionRow,
  type CoachSessionRowProps,
} from "~/components/engagement/coach-session-row";
import { type TimelineSessionItem } from "~/components/engagement/coach-session-timeline-view";
import { useReducedMotionSafe } from "~/lib/hooks/use-reduced-motion-safe";
import { hearthEntrance } from "~/lib/shared/hearth-entrance";

import {
  SHOWCASE_TIMELINE_PROGRESS,
  getShowcaseTimelinePast,
  getShowcaseTimelineUpcoming,
} from "./showcase-fixtures";
import { ShowcaseFrame } from "./showcase-frame";

const noop = (): void => undefined;

/** A demo row + its hover/focus reveal of notes & homework counts. */
function ShowcaseSessionRow({
  session,
  variant,
  order,
  reduce,
}: {
  readonly session: TimelineSessionItem;
  readonly variant: CoachSessionRowProps["variant"];
  readonly order: number;
  readonly reduce: boolean;
}): ReactNode {
  const noteCount = (session.notes?.coach ?? 0) + (session.notes?.client ?? 0);
  const homeworkCount = session.homeworkCount ?? 0;
  const hasReveal = noteCount > 0 || homeworkCount > 0;

  return (
    <motion.div {...hearthEntrance(order, reduce)} className="group">
      <CoachSessionRow
        variant={variant}
        sessionNumber={session.sessionNumber}
        title={session.title}
        startAt={session.startAt}
        duration={session.duration}
        status={session.status}
        goalDescription={session.goalDescription}
        hasMeetingUrl={session.hasMeetingUrl}
        notes={session.notes}
        homeworkCount={session.homeworkCount}
        onEdit={noop}
        onJoin={session.hasMeetingUrl ? noop : undefined}
        onViewNotes={variant === "past" ? noop : undefined}
      />

      {hasReveal && (
        <div className="grid grid-rows-[0fr] overflow-hidden transition-[grid-template-rows] duration-300 ease-out group-focus-within:grid-rows-[1fr] group-hover:grid-rows-[1fr]">
          <div className="min-h-0">
            <div className="mx-4 mt-1 flex flex-wrap items-center gap-2 rounded-lg bg-purple-50/70 px-3 py-2 text-sm text-gray-600">
              {noteCount > 0 && (
                <span>{`${noteCount} note${noteCount === 1 ? "" : "s"} captured`}</span>
              )}
              {noteCount > 0 && homeworkCount > 0 && (
                <span aria-hidden="true" className="text-gray-300">
                  ·
                </span>
              )}
              {homeworkCount > 0 && (
                <span>{`${homeworkCount} action${homeworkCount === 1 ? "" : "s"} assigned`}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export function SessionTimelineShowcase(): ReactNode {
  const reduce = useReducedMotionSafe();
  // Computed at render time (not module scope) so SSR sees the request clock;
  // memoized so the timeline's dates stay stable across re-renders.
  const upcoming = useMemo(() => getShowcaseTimelineUpcoming(), []);
  const past = useMemo(() => getShowcaseTimelinePast(), []);

  return (
    <ShowcaseFrame variant="feature">
      <div className="flex flex-col gap-6 bg-gray-50/60 p-4">
        <div>
          {/* h3, not h1: this replica renders inside a marketing section whose
              real heading is an h2 — the page's single h1 is the hero's. */}
          <h3 className="font-heading text-2xl font-medium text-gray-900">
            Sessions
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {SHOWCASE_TIMELINE_PROGRESS.sessionsCompleted} of{" "}
            {SHOWCASE_TIMELINE_PROGRESS.sessionsTotal} sessions
          </p>
        </div>

        <section className="flex flex-col gap-3">
          <h4 className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            Upcoming
          </h4>
          {upcoming.map((session, index) => (
            <ShowcaseSessionRow
              key={session.id}
              session={session}
              variant="upcoming"
              order={index}
              reduce={reduce}
            />
          ))}
        </section>

        <section className="flex flex-col gap-3">
          <h4 className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            Past
          </h4>
          {past.map((session, index) => (
            <ShowcaseSessionRow
              key={session.id}
              session={session}
              variant="past"
              order={upcoming.length + index}
              reduce={reduce}
            />
          ))}
        </section>
      </div>
    </ShowcaseFrame>
  );
}
